import { useCallback, useEffect, useMemo, useState } from 'react'
import { IconTruck } from '@tabler/icons-react'
import classNames from 'classnames'
import { CustomLocation } from 'shared-types'
import Card from '~/components/Card/Card'
import { LocationSearch } from '~/components/LocationSearch'
import { ProductDeliverToPostcodeStatusCardProps } from '~/components/ProductDeliverToPostcodeStatusCard/ProductDeliverToPostcodeStatusCard.types'
import Status from '~/components/Status/Status'
import Typography, {
  TypographyTag,
  TypographyVariant,
} from '~/components/Typography'

import { getDeliveryInfo } from '~/helpers/deliveryHelpers'
import {
  useCustomer,
  usePage,
  useSession,
  useUserDeliveryPostcode,
} from '~/hooks'
import { DeliveryPostcodeProps } from '../PostcodeConfigureDrawer/PostcodeConfigureDrawer.types'

export const ProductDeliverToPostcodeStatusCard = ({
  sku,
  freightServiceMessage,
  freightServiceErrorCode,
  productPrice,
}: ProductDeliverToPostcodeStatusCardProps) => {
  const {
    promptUserPostcodeDrawer,
    findDeliveryOptionsNoPrompt,
    deliveryMethods,
    postcode: providerPostcode,
    freightServiceMessage: providerFreightServiceMessage,
    freightServiceErrorCode: providerFreightErrorCode,
    city: providerCity,
    sku: providerSku,
    state,
  } = useUserDeliveryPostcode()

  const { customer } = useCustomer()
  const page = usePage()
  const { session } = useSession()
  const [showPostalCodeInput, setShowPostalCodeInput] = useState(true)

  useEffect(() => {
    findDeliveryOptionsNoPrompt('', 0, true)
  }, [findDeliveryOptionsNoPrompt])

  const handleShowPostalCodeInput = () => {
    setShowPostalCodeInput(!showPostalCodeInput)
  }

  const freightServiceResponse = useMemo(() => {
    return freightServiceMessage || providerFreightServiceMessage
  }, [freightServiceMessage, providerFreightServiceMessage])

  const freightErrorResponse = useMemo(() => {
    return freightServiceErrorCode || providerFreightErrorCode
  }, [freightServiceErrorCode, providerFreightErrorCode])

  const handleChangePostcode = useCallback(
    (value: DeliveryPostcodeProps) => {
      findDeliveryOptionsNoPrompt(sku, productPrice)

      session.token.deliveryPostcode = value.postcode
      session.token.deliveryCity = value.city
      session.token.deliveryState = value.state
    },
    [findDeliveryOptionsNoPrompt, sku, productPrice, session?.token]
  )

  useEffect(() => {
    if (
      providerPostcode &&
      deliveryMethods?.length === 0 &&
      showPostalCodeInput &&
      !providerFreightErrorCode
    ) {
      setShowPostalCodeInput(false)
      handleChangePostcode({
        postcode: providerPostcode,
        city: providerCity,
        state,
      })
    }
  }, [
    deliveryMethods,
    handleChangePostcode,
    providerCity,
    providerFreightErrorCode,
    providerPostcode,
    showPostalCodeInput,
    state,
  ])

  return (
    <Card
      title={
        <button
          aria-label='Add postcode for delivery availability'
          type='button'
          className='bg-transparent'
          onClick={() => {
            return handleShowPostalCodeInput()
          }}>
          Deliver to{' '}
          <span className='underline'>
            {providerCity}, {providerPostcode}
          </span>
        </button>
      }
      variant='default'
      icon={<IconTruck size={24} />}>
      <div
        className={classNames('mt-2', {
          hidden: !showPostalCodeInput,
        })}>
        <LocationSearch
          placeholder='Enter postcode or suburb for delivery options'
          name='search'
          type='postcode'
          absoluteDropdown={false}
          value={providerPostcode}
          sku={providerSku}
          onLocationSelected={(location?: CustomLocation) => {
            handleChangePostcode({
              postcode: location.postcode,
              city: location.suburb,
              state: location.state,
            })
          }}
        />
      </div>
      <div className='mt-2'>
        {freightErrorResponse === 0 &&
        deliveryMethods &&
        deliveryMethods?.length > 0 ? (
          deliveryMethods.map((method, index) => {
            const shippingMethod = getDeliveryInfo(method, page?.countryCode)
            return (
              <button
                key={index}
                aria-label='Add postcode for delivery availability'
                type='button'
                className='border rounded-md bg-transparent hover:bg-gray-200 w-full p-4 text-left'
                onClick={() => {
                  return promptUserPostcodeDrawer(sku, productPrice)
                }}>
                <Status
                  label={
                    <div className='flex justify-between'>
                      <div>
                        <div className='flex items-start gap-2'>
                          <Typography
                            tag={TypographyTag.p}
                            variant={TypographyVariant.BodyRegularBold}
                            className='text-secondary-muted flex flex justify-center items-start'>
                            <span className='mt-[-2px]'>
                              {shippingMethod.deliveryLabel}
                            </span>
                            {method.deliveryLabel === 'Fast' && (
                              <span
                                className={classNames(
                                  'ml-1 mt-[-7px] text-[14px] text-white px-[8px] py-[4px] rounded bg-[#0519F1]'
                                )}>
                                {shippingMethod.isFastest ? 'Fastest' : 'Fast'}
                              </span>
                            )}
                            {customer?.customerGroup?.key &&
                              shippingMethod.deliveryLabel === 'Standard' &&
                              !shippingMethod.isBulkyItem && (
                                <span
                                  className={classNames(
                                    'ml-1 mt-[-7px] text-[14px] text-white px-[8px] py-[4px] rounded bg-[var(--status-color-bg-success)]'
                                  )}>
                                  Free Over $149
                                </span>
                              )}
                          </Typography>
                        </div>
                        {shippingMethod.deliveryDescription && (
                          <Typography
                            tag={TypographyTag.p}
                            variant={TypographyVariant.BodySmallBold}
                            className='mt-1'>
                            <div
                              dangerouslySetInnerHTML={{
                                __html: shippingMethod.deliveryDescription,
                              }}
                            />
                          </Typography>
                        )}
                        {method.deliverySurcharge > 0 && (
                          <Typography
                            tag={TypographyTag.div}
                            variant={TypographyVariant.BodySmallBold}
                            className='mt-1'>
                            *Fuel Surcharge may apply
                          </Typography>
                        )}
                      </div>
                      {!!shippingMethod.price && (
                        <Typography
                          tag={TypographyTag.p}
                          variant={TypographyVariant.BodyRegularBold}
                          className='text-secondary-muted'>
                          ${shippingMethod.price}
                        </Typography>
                      )}
                    </div>
                  }
                  status='success'
                />
              </button>
            )
          })
        ) : (
          <Status
            label={freightServiceResponse}
            status={freightErrorResponse > 0 ? 'error' : null}
          />
        )}
      </div>
    </Card>
  )
}
